// Third party
import { createReducer, on, Action } from '@ngrx/store';

// Local file
import * as AuthActions from './auth.actions';
import { IAuthState } from './auth.models';



export const AUTH_FEATURE_KEY = 'auth';
export interface AuthPartialState {
  readonly [AUTH_FEATURE_KEY]: IAuthState;
}

const stateGuard = (state: IAuthState): IAuthState => state;

const reducer = createReducer(
  {},
  on(
    AuthActions.logoutAPISuccess,
    () => ({
      logout: {
        isSuccessLogout: true
      }
    })
  ),
  on(
    AuthActions.initAuthState,
    AuthActions.clearAuthState,
    () => stateGuard({
      id: 'auth-state',
      name: 'auth',
    })
  ),
  on(AuthActions.loadAuthStateSuccess, (state) => stateGuard({
    ...state as IAuthState,
    isLoaded: true,
  })),
  on(AuthActions.loadAuthStateFailure, (state, { error }) => stateGuard({
    ...state as IAuthState,
    error
  })),

  /**
   * GET API Request
   */
  on(AuthActions.registerAPI, (state) => stateGuard({
    ...state as IAuthState,
    register: {
      ...(state as IAuthState).register,
      isRegistering: true,
      isSuccessRegister: undefined,
      response: undefined
    },
    error: undefined
  })),

  on(AuthActions.jurisdictionsAPI, (state) => stateGuard({
    ...state as IAuthState,
    jurisdictions: {
      ...(state as IAuthState).jurisdictions,
      isProcessing: true,
      isSuccessProcess: undefined,
      response: undefined
    },
    error: undefined
  })),

  on(AuthActions.completeRegistrationAPI, (state) => stateGuard({
    ...state as IAuthState,
    completeRegistration: {
      ...(state as IAuthState).register,
      isCompleteRegistering: true,
      isSuccessCompleteRegistration: undefined,
      response: undefined
    },
    error: undefined
  })),

  on(AuthActions.uploadDocumentsAPI, (state) => stateGuard({
    ...state as IAuthState,
    documentsVerification: {
      ...(state as IAuthState).documentsVerification,
      isDocumentsVerificating: true,
      isSuccessDocumentsVerification: undefined,
      response: undefined
    },
    error: undefined
  })),

  on(AuthActions.renewPasswordAPI, (state) => stateGuard({
    ...state as IAuthState,
    forgotPassword: {
      ...(state as IAuthState).forgotPassword,
      isRenewingPassword: true,
      isSuccessRenewPassword: undefined,
      response: undefined
    },
    error: undefined
  })),

  on(AuthActions.updatePasswordAPI, (state) => stateGuard({
    ...state as IAuthState,
    updatePassword: {
      ...(state as IAuthState).updatePassword,
      isUpdatingPassword: true,
      isSuccessUpdatePassword: undefined,
      response: undefined
    },
    error: undefined
  })),

  on(AuthActions.loggingInAPI, (state) => stateGuard({
    ...state as IAuthState,
    login: {
      ...(state as IAuthState).login,
      isLoggingIn: true,
      isSuccessLogin: undefined,
      response: undefined
    },
    error: undefined
  })),
  on(AuthActions.loggingOutAPI, (state) => stateGuard({
    ...state as IAuthState,
    logout: {
      ...(state as IAuthState).logout,
      isLoggingOut: true,
      isSuccessLogout: undefined,
      response: undefined
    },
    error: undefined
  })),

  /**
   * GET API Response SUCCESS
   */
  on(AuthActions.renewPasswordAPISuccess, (state, { successResponse }) => stateGuard({
    ...state as IAuthState,
    forgotPassword: {
      ...(state as IAuthState).forgotPassword,
      isRenewingPassword: false,
      isSuccessRenewPassword: true,
      response: successResponse
    },
  })),

  on(AuthActions.uploadDocumentsAPISuccess, (state, { successResponse }) => stateGuard({
    ...state as IAuthState,
    documentsVerification: {
      ...(state as IAuthState).documentsVerification,
      isDocumentsVerificating: false,
      isSuccessDocumentsVerification: true,
      response: successResponse
    }
  })),

  on(AuthActions.updatePasswordAPISuccess, (state, { successResponse }) => stateGuard({
    ...state as IAuthState,
    updatePassword: {
      ...(state as IAuthState).updatePassword,
      isUpdatingPassword: false,
      isSuccessUpdatePassword: true,
      response: successResponse
    }
  })),

  on(AuthActions.loginAPISuccess, (state, { successResponse, username }) => stateGuard({
    ...state as IAuthState,
    login: {
      ...(state as IAuthState).login,
      isLoggingIn: false,
      isSuccessLogin: true,
      response: successResponse,
      username
    },
    logout: undefined
  })),

  on(AuthActions.registerAPISuccess, (state, { successResponse }) => stateGuard({
    ...state as IAuthState,
    register: {
      ...(state as IAuthState).register,
      isRegistering: false,
      isSuccessRegister: true,
      response: successResponse
    }
  })),

  on(AuthActions.jurisdictionsAPISuccess, (state, { successResponse }) => stateGuard({
    ...state as IAuthState,
    jurisdictions: {
      ...(state as IAuthState).jurisdictions,
      isProcessing: false,
      isSuccessProcess: true,
      response: successResponse
    },
  })),

  on(AuthActions.completeRegistrationAPISuccess, (state, { successResponse }) => stateGuard({
    ...state as IAuthState,
    completeRegistration: {
      ...(state as IAuthState).completeRegistration,
      isCompleteRegistering: false,
      isSuccessCompleteRegistration: true,
      response: successResponse
    },
  })),

  /**
   * GET API Response ERROR
   */
  on(AuthActions.registerAPIFailed, (state, { errorResponse }) => stateGuard({
    ...state as IAuthState,
    register : {
      ...(state as IAuthState).register,
      isRegistering: false,
      isSuccessRegister: false,
      response: errorResponse
    },
    error: errorResponse
  })),

  on(AuthActions.jurisdictionsAPIFailed, (state, { errorResponse }) => stateGuard({
    ...state as IAuthState,
    jurisdictions : {
      ...(state as IAuthState).jurisdictions,
      isProcessing: false,
      isSuccessProcess: false,
      response: errorResponse
    },
    error: errorResponse
  })),

  on(AuthActions.completeRegistrationAPIFailed, (state, { errorResponse }) => stateGuard({
    ...state as IAuthState,
    completeRegistration : {
      ...(state as IAuthState).completeRegistration,
      isCompleteRegistering: false,
      isSuccessCompleteRegistration: false,
      response: errorResponse
    },
    error: errorResponse
  })),

  on(AuthActions.uploadDocumentsAPIFailed, (state, { errorResponse }) => stateGuard({
    ...state as IAuthState,
    documentsVerification : {
      ...(state as IAuthState).documentsVerification,
      isDocumentsVerificating: false,
      isSuccessDocumentsVerification: false,
      response: errorResponse
    },
    error: errorResponse
  })),

  on(AuthActions.renewPasswordAPIFailed, (state, { errorResponse }) => stateGuard({
    ...state as IAuthState,
    forgotPassword : {
      ...(state as IAuthState).forgotPassword,
      isRenewingPassword: false,
      isSuccessRenewPassword: false,
      response: errorResponse
    },
    error: errorResponse
  })),

  on(AuthActions.updatePasswordAPIFailed, (state, { errorResponse }) => stateGuard({
    ...state as IAuthState,
    updatePassword : {
      ...(state as IAuthState).updatePassword,
      isUpdatingPassword: false,
      isSuccessUpdatePassword: false,
      response: errorResponse
    },
    error: errorResponse
  })),

  on(AuthActions.loginAPIFailed, (state, { errorResponse }) => stateGuard({
    ...state as IAuthState,
    login : {
      ...(state as IAuthState).login,
      isLoggingIn: false,
      isSuccessLogin: false,
      response: errorResponse
    },
    error: errorResponse
  })),
  on(AuthActions.logoutAPIFailed, (state, { errorResponse }) => stateGuard({
    ...state as IAuthState,
    logout: {
      ...(state as IAuthState).logout,
      isLoggingOut: false,
      isSuccessLogout: false,
      response: errorResponse
    },
    error: errorResponse
  })),

  /**
   * Properties
   */
  on(AuthActions.setIsAuthenticated, (state, { isAuthenticated }) => stateGuard({
    ...state as IAuthState,
    isAuthenticated
  })),

  on(AuthActions.setIsRememberMe, (state, { isRememberMe }) => stateGuard({
    ...state as IAuthState,
    login : {
      ...(state as IAuthState).login,
      isRememberMe
    },
  })),

  on(AuthActions.setUserFullName, (state, { isFullName }) => stateGuard({
    ...state as IAuthState,
    user : {
      ...(state as IAuthState).user,
      isFullName
    },
  })),

  on(AuthActions.setIdFile, (state, { idFile }) => stateGuard({
    ...state as IAuthState,
    documentsVerification : {
      ...(state as IAuthState).documentsVerification,
      idFile
    },
  })),

  on(AuthActions.setBillFile, (state, { billFile }) => stateGuard({
    ...state as IAuthState,
    documentsVerification : {
      ...(state as IAuthState).documentsVerification,
      billFile
    },
  })),

  on(AuthActions.setCountryStates, (state, { subdivisions }) => stateGuard({
    ...state as IAuthState,
    jurisdictions : {
      ...(state as IAuthState).jurisdictions,
      subdivisions
    },
  })),

  on(AuthActions.setError, (state, { error }) => stateGuard({
    ...state as IAuthState,
    error
  })),
);

export function authReducer(state: IAuthState | undefined, action: Action) {
  return reducer(state, action);
}
